import {
  addUnit,
  alignStart,
  stepUnits,
  toTime,
} from "./timelineDateUtils";

/** Fixed track width (px) for the standard EmployeeTimeline layout. */
export const TRACK_WIDTH_PX = 720;
export const COMPACT_TRACK_WIDTH_PX = 480;
export const LABEL_COLUMN_PX = 200;
export const COMPACT_LABEL_COLUMN_PX = 140;

export const SCALE_OPTIONS = [
  { value: "years", label: "Years" },
  { value: "quarters", label: "Quarters" },
  { value: "months", label: "Months" },
];

export const SCALE_CONFIG = {
  years: { unitCount: 4, unitWidthPx: 180, shiftUnits: 2 },
  quarters: { unitCount: 12, unitWidthPx: 60, shiftUnits: 6 },
  months: { unitCount: 18, unitWidthPx: 40, shiftUnits: 9 },
};

const VALID_SCALES = new Set(Object.keys(SCALE_CONFIG));

export function normalizeScale(raw) {
  const key = String(raw || "")
    .toLowerCase()
    .trim();
  return VALID_SCALES.has(key) ? key : "years";
}

export function trackWidthForScale(scale, compact) {
  const cfg = SCALE_CONFIG[normalizeScale(scale)];
  const width = cfg.unitCount * cfg.unitWidthPx;
  if (compact) return COMPACT_TRACK_WIDTH_PX;
  return width;
}

/** Default window start: today’s unit minus half the visible window. */
export function defaultWindowStartMs(todayMs, scale) {
  const z = normalizeScale(scale);
  const cfg = SCALE_CONFIG[z];
  const todayUnit = alignStart(todayMs ?? Date.now(), z);
  return stepUnits(todayUnit, z, -cfg.shiftUnits);
}

export function windowEndMs(windowStartMs, scale) {
  const z = normalizeScale(scale);
  const cfg = SCALE_CONFIG[z];
  let end = windowStartMs;
  for (let i = 0; i < cfg.unitCount; i += 1) {
    end = addUnit(end, z);
  }
  return end;
}

/**
 * Map a timestamp to x (px) within the window.
 * x = (date − window start) ÷ window length × track width
 */
export function dateToX(timeMs, windowStartMs, windowEndMs, trackWidthPx) {
  if (timeMs == null || windowEndMs <= windowStartMs) return 0;
  const frac = (timeMs - windowStartMs) / (windowEndMs - windowStartMs);
  return Math.min(trackWidthPx, Math.max(0, frac * trackWidthPx));
}

export function buildAxisUnits(windowStartMs, scale) {
  const z = normalizeScale(scale);
  const cfg = SCALE_CONFIG[z];
  const units = [];
  let t = windowStartMs;
  for (let i = 0; i < cfg.unitCount; i += 1) {
    units.push({ startMs: t, index: i, widthPx: cfg.unitWidthPx });
    t = addUnit(t, z);
  }
  return units;
}

export function unitContainingToday(windowStartMs, todayMs, scale) {
  const z = normalizeScale(scale);
  const todayUnit = alignStart(todayMs, z);
  const units = buildAxisUnits(windowStartMs, z);
  return units.find((u) => u.startMs === todayUnit) ?? null;
}

export function shiftWindowStart(windowStartMs, scale, direction) {
  const z = normalizeScale(scale);
  const cfg = SCALE_CONFIG[z];
  const delta = direction === "forward" ? cfg.shiftUnits : -cfg.shiftUnits;
  return stepUnits(windowStartMs, z, delta);
}

export function isTodayInWindow(todayMs, windowStartMs, windowEndMs) {
  const t = toTime(todayMs);
  if (t == null) return false;
  return t >= windowStartMs && t < windowEndMs;
}

export function todayLineX(todayMs, windowStartMs, windowEndMs, trackWidthPx) {
  return dateToX(toTime(todayMs), windowStartMs, windowEndMs, trackWidthPx);
}
